/**
 * Artifact Detail Editor
 * 
 * Reusable component for editing artifact details
 * Can be used in tabs, cards, or dialogs
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Form, Input, Select, Card, message, Button, Space, Collapse, Tabs } from 'antd';
import { SaveOutlined, EditOutlined, EyeOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { apiClient } from '../../../services/apiClient';
import { PrimeArtifact } from '../../../types/primeArtifact';
import { useArtifactTypes } from '../../../hooks/useArtifactTypes';
import ReactMarkdown from 'react-markdown';
import './EditorStyles.css';

const { TextArea } = Input;
const { Option } = Select;
const { Panel } = Collapse;

interface ArtifactDetailEditorProps {
  artifactId: string;
  onSave?: () => void;
  embedded?: boolean; // If true, no save button (parent handles save)
  onChange?: (values: any) => void; // Callback for embedded mode
}

export const ArtifactDetailEditor: React.FC<ArtifactDetailEditorProps> = ({
  artifactId,
  onSave,
  embedded = false,
  onChange
}) => {
  const [form] = Form.useForm();
  const { artifactTypes } = useArtifactTypes();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [artifactData, setArtifactData] = useState<PrimeArtifact | null>(null);
  const [parentArtifacts, setParentArtifacts] = useState<any[]>([]);
  const [stateTemplates, setStateTemplates] = useState<Array<{ id: string; name: string; version?: string }>>([]);
  const [loadingStateTemplates, setLoadingStateTemplates] = useState(false);
  const [descriptionPreview, setDescriptionPreview] = useState<string>('');
  
  // Loading states for apply operations
  const [isApplyingAll, setIsApplyingAll] = useState(false);
  const [isApplyingStates, setIsApplyingStates] = useState(false);
  const [isApplyingTransitions, setIsApplyingTransitions] = useState(false);
  const [isApplyingProperties, setIsApplyingProperties] = useState(false);

  const loadStateTemplates = useCallback(async () => {
    try {
      setLoadingStateTemplates(true);
      const response = await apiClient.get('/state-templates?include_deleted=false');
      const templates = response.data || [];
      setStateTemplates(templates.map((t: any) => ({
        id: t.id,
        name: t.name,
        version: t.version
      })));
    } catch (error) {
      console.error('Error loading state templates:', error);
      setStateTemplates([]);
    } finally {
      setLoadingStateTemplates(false);
    }
  }, []);

  useEffect(() => {
    loadArtifactData();
    loadParentArtifacts();
    loadStateTemplates();
  }, [artifactId, loadStateTemplates]);

  // Update form values when artifactData changes (after form is mounted)
  useEffect(() => {
    if (artifactData && !loading) {
      // Use a small delay to ensure form is fully mounted
      const timer = setTimeout(() => {
        try {
          const description = artifactData.description || '';
          form.setFieldsValue({
            name: artifactData.name,
            description: description,
            artifact_type_id: artifactData.artifact_type_id,
            parent_ref: artifactData.parent_ref || '',
            state_template_id: artifactData.state_template_id || '',
            creation_mode: artifactData.creation_mode || 'any',
          });
          setDescriptionPreview(description);
        } catch (formError) {
          // Form might not be ready yet, ignore silently
          // This is expected in React StrictMode during development
        }
      }, 10);
      return () => clearTimeout(timer);
    }
  }, [artifactData, loading, form]);

  // Watch description field changes for preview
  const descriptionValue = Form.useWatch('description', form);
  useEffect(() => {
    if (descriptionValue !== undefined) {
      setDescriptionPreview(descriptionValue || '');
    }
  }, [descriptionValue]);

  const loadArtifactData = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/prime-artifacts/${artifactId}`);
      setArtifactData(response.data);
    } catch (error) {
      console.error('Error loading artifact data:', error);
      message.error('Failed to load artifact data');
    } finally {
      setLoading(false);
    }
  };

  const loadParentArtifacts = async () => {
    try {
      // Load all artifacts with pagination to get complete list
      const allArtifacts: any[] = [];
      let skip = 0;
      const limit = 100;
      let hasMore = true;

      while (hasMore) {
        const response = await apiClient.get(`/prime-artifacts?skip=${skip}&limit=${limit}`);
        const artifacts = Array.isArray(response.data) ? response.data : [];
        allArtifacts.push(...artifacts);
        
        // If we got fewer artifacts than the limit, we've reached the end
        if (artifacts.length < limit) {
          hasMore = false;
        } else {
          skip += limit;
        }
      }
      
      setParentArtifacts(allArtifacts);
    } catch (error) {
      console.error('Error loading parent artifacts:', error);
    }
  };

  const handleSave = async (values: any) => {
    try {
      setSaving(true);
      
      if (embedded && onChange) {
        onChange(values);
        return;
      }

      // Prepare update payload - only include defined fields and handle null/empty strings
      const updatePayload: any = {
        name: values.name,
        description: values.description || null,
      };

      // Only include artifact_type_id if it's not empty
      if (values.artifact_type_id && values.artifact_type_id.trim() !== '') {
        updatePayload.artifact_type_id = values.artifact_type_id;
      } else if (values.artifact_type_id === '' || values.artifact_type_id === null || values.artifact_type_id === undefined) {
        // Explicitly set to null if empty/null/undefined
        updatePayload.artifact_type_id = null;
      }

      // Handle parent_ref
      if (values.parent_ref === '' || values.parent_ref === null || values.parent_ref === undefined) {
        updatePayload.parent_ref = null;
      } else {
        updatePayload.parent_ref = values.parent_ref;
      }

      // Handle state_template_id - always include it if present in form values
      if (values.state_template_id !== undefined) {
        if (values.state_template_id === '' || values.state_template_id === null) {
          updatePayload.state_template_id = null;
        } else if (values.state_template_id.trim() !== '') {
          updatePayload.state_template_id = values.state_template_id.trim();
        }
      }

      // Handle creation_mode - default to 'any' if not set
      if (values.creation_mode !== undefined) {
        if (values.creation_mode === '' || values.creation_mode === null) {
          updatePayload.creation_mode = 'any';
        } else {
          updatePayload.creation_mode = values.creation_mode;
        }
      }

      console.log('[ArtifactDetailEditor] Sending update payload:', JSON.stringify(updatePayload, null, 2));
      console.log('[ArtifactDetailEditor] artifact_type_id value:', updatePayload.artifact_type_id, 'type:', typeof updatePayload.artifact_type_id);

      await apiClient.put(`/prime-artifacts/${artifactId}`, updatePayload);

      message.success('Artifact updated successfully');
      
      if (onSave) {
        onSave();
      }
    } catch (error: any) {
      console.error('Error saving artifact:', error);
      console.error('Error response:', error?.response?.data);
      console.error('Error status:', error?.response?.status);
      const errorMessage = error?.response?.data?.detail || error?.message || 'Unknown error';
      message.error(`Failed to save artifact: ${errorMessage}`);
    } finally {
      setSaving(false);
    }
  };

  // Refresh artifact data after apply operations
  const refreshArtifactData = async () => {
    try {
      await loadArtifactData();
    } catch (error) {
      console.error('[ArtifactDetailEditor] Error refreshing artifact data:', error);
    }
  };

  // Apply All handler
  const handleApplyAll = async () => {
    if (!artifactId) return;

    setIsApplyingAll(true);
    try {
      let response;
      let data;
      
      // Check if state_template_id exists - use state template endpoint
      if (artifactData?.state_template_id) {
        response = await apiClient.post(
          `/prime-artifacts/${artifactId}/apply-state-template`,
          {
            template_id: artifactData.state_template_id,
            reset_existing: false
          }
        );
        data = response.data;
      } else {
        // Fallback to artifact type template
        response = await apiClient.post(`/prime-artifacts/${artifactId}/apply-template`);
        data = response.data;
      }
      
      const counts = [];
      if (data.states_created_count !== undefined) counts.push(`${data.states_created_count} states`);
      else if (data.states_created !== undefined) counts.push(`${data.states_created} states`);
      if (data.transitions_created_count !== undefined) counts.push(`${data.transitions_created_count} transitions`);
      else if (data.transitions_created !== undefined) counts.push(`${data.transitions_created} transitions`);
      if (data.properties_created_count !== undefined) counts.push(`${data.properties_created_count} properties`);
      else if (data.properties_created !== undefined) counts.push(`${data.properties_created} properties`);
      
      const messageText = counts.length > 0 
        ? `Applied template: ${counts.join(', ')}`
        : 'Template applied successfully';
      
      message.success(messageText);
      
      // Refresh artifact data
      await refreshArtifactData();
    } catch (error: any) {
      console.error('[ArtifactDetailEditor] Error applying all:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to apply template';
      message.error(`Failed to apply template: ${errorMessage}`);
    } finally {
      setIsApplyingAll(false);
    }
  };

  // Apply States handler
  const handleApplyStates = async () => {
    if (!artifactId) return;

    setIsApplyingStates(true);
    try {
      let response;
      let data;
      
      // Check if state_template_id exists - use state template endpoint
      if (artifactData?.state_template_id) {
        response = await apiClient.post(
          `/prime-artifacts/${artifactId}/apply-state-template`,
          {
            template_id: artifactData.state_template_id,
            reset_existing: false
          }
        );
        data = response.data;
      } else {
        // Fallback to artifact type template
        response = await apiClient.post(`/prime-artifacts/${artifactId}/apply-template`);
        data = response.data;
      }
      
      // Report on states (note that other components may also be applied)
      const statesCount = data.states_created_count !== undefined 
        ? data.states_created_count 
        : (data.states_created || 0);
      
      if (statesCount > 0) {
        message.success(`Applied ${statesCount} states from template`);
      } else {
        message.info('No new states were created (they may already exist)');
      }
      
      // Refresh artifact data
      await refreshArtifactData();
    } catch (error: any) {
      console.error('[ArtifactDetailEditor] Error applying states:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to apply states';
      message.error(`Failed to apply states: ${errorMessage}`);
    } finally {
      setIsApplyingStates(false);
    }
  };

  // Apply Transitions handler
  const handleApplyTransitions = async () => {
    if (!artifactId) return;

    setIsApplyingTransitions(true);
    try {
      let response;
      let data;
      
      // Check if state_template_id exists - use state template endpoint
      if (artifactData?.state_template_id) {
        response = await apiClient.post(
          `/prime-artifacts/${artifactId}/apply-state-template`,
          {
            template_id: artifactData.state_template_id,
            reset_existing: false
          }
        );
        data = response.data;
      } else {
        // Fallback to artifact type template
        response = await apiClient.post(`/prime-artifacts/${artifactId}/apply-template`);
        data = response.data;
      }
      
      // Report on transitions (note that other components may also be applied)
      const transitionsCount = data.transitions_created_count !== undefined 
        ? data.transitions_created_count 
        : (data.transitions_created || 0);
      
      if (transitionsCount > 0) {
        message.success(`Applied ${transitionsCount} transitions from template`);
      } else {
        message.info('No new transitions were created (they may already exist)');
      }
      
      // Refresh artifact data
      await refreshArtifactData();
    } catch (error: any) {
      console.error('[ArtifactDetailEditor] Error applying transitions:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to apply transitions';
      message.error(`Failed to apply transitions: ${errorMessage}`);
    } finally {
      setIsApplyingTransitions(false);
    }
  };

  // Apply Properties handler
  const handleApplyProperties = async () => {
    if (!artifactId) return;

    setIsApplyingProperties(true);
    try {
      let response;
      let data;
      
      // Check if state_template_id exists - use state template endpoint
      if (artifactData?.state_template_id) {
        response = await apiClient.post(
          `/prime-artifacts/${artifactId}/apply-state-template`,
          {
            template_id: artifactData.state_template_id,
            reset_existing: false
          }
        );
        data = response.data;
        
        // Extract properties count from state template response
        const propertiesCount = data.properties_created_count !== undefined 
          ? data.properties_created_count 
          : (data.properties_created || 0);
        const skippedCount = data.properties_skipped || 0;
        
        let messageText = `Applied ${propertiesCount} properties from state template`;
        if (skippedCount > 0) {
          messageText += ` (${skippedCount} already existed)`;
        }
        
        message.success(messageText);
      } else {
        // Fallback to artifact type template properties endpoint
        response = await apiClient.post(`/prime-artifacts/${artifactId}/apply-properties`, {
          property_source: 'type'
        });
        data = response.data;
        
        const propertiesCount = data.properties_created || 0;
        const skippedCount = data.properties_skipped || 0;
        
        let messageText = `Applied ${propertiesCount} properties from template`;
        if (skippedCount > 0) {
          messageText += ` (${skippedCount} already existed)`;
        }
        
        message.success(messageText);
      }
      
      // Refresh artifact data
      await refreshArtifactData();
    } catch (error: any) {
      console.error('[ArtifactDetailEditor] Error applying properties:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to apply properties';
      message.error(`Failed to apply properties: ${errorMessage}`);
    } finally {
      setIsApplyingProperties(false);
    }
  };

  if (loading) {
    return (
      <div className="editor-loading">
        Loading artifact data...
      </div>
    );
  }

  const isAnyApplying = isApplyingAll || isApplyingStates || isApplyingTransitions || isApplyingProperties;

  return (
    <div className="artifact-detail-editor">
      <Card className="editor-card">
        <div className="editor-card-head">
          <Space>
            {!embedded && (
              <Button 
                type="primary" 
                onClick={() => form.submit()}
                loading={saving}
                icon={<SaveOutlined />}
              >
                Save Changes
              </Button>
            )}
            <Button 
              type="primary"
              onClick={handleApplyAll}
              disabled={isAnyApplying || saving}
              loading={isApplyingAll}
            >
              {isApplyingAll ? 'Applying...' : 'Apply All'}
            </Button>
            <Button 
              onClick={handleApplyStates}
              disabled={isAnyApplying || saving}
              loading={isApplyingStates}
            >
              {isApplyingStates ? 'Applying...' : 'Apply States'}
            </Button>
            <Button 
              onClick={handleApplyTransitions}
              disabled={isAnyApplying || saving}
              loading={isApplyingTransitions}
            >
              {isApplyingTransitions ? 'Applying...' : 'Apply Transitions'}
            </Button>
            <Button 
              onClick={handleApplyProperties}
              disabled={isAnyApplying || saving}
              loading={isApplyingProperties}
            >
              {isApplyingProperties ? 'Applying...' : 'Apply Properties'}
            </Button>
          </Space>
        </div>
        <div className="editor-card-body">
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSave}
            initialValues={{
              name: artifactData?.name || '',
              description: artifactData?.description || '',
              artifact_type_id: artifactData?.artifact_type_id,
              parent_ref: artifactData?.parent_ref || '',
              state_template_id: artifactData?.state_template_id || '',
            }}
          >
            <div className="editor-section">
              {/* Consolidated top row: Name, Artifact Type, Parent */}
              <div className="details-top-row" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem', marginBottom: '1.5rem' }}>
                <Form.Item
                  label="Name"
                  name="name"
                  rules={[{ required: true, message: 'Please enter artifact name' }]}
                >
                  <Input placeholder="Enter artifact name" />
                </Form.Item>

                <Form.Item
                  label="Artifact Type"
                  name="artifact_type_id"
                  rules={[{ required: true, message: 'Please select artifact type' }]}
                >
                  <Select placeholder="Select artifact type">
                    {artifactTypes
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((type) => (
                        <Option key={type.id} value={type.id}>
                          {type.name}
                        </Option>
                      ))}
                  </Select>
                </Form.Item>

                <Form.Item
                  label="Parent Artifact"
                  name="parent_ref"
                >
                  <Select 
                    placeholder="Select parent artifact (optional)"
                    allowClear
                    showSearch
                    filterOption={(input, option) => {
                      const label = typeof option?.children === 'string' 
                        ? option.children 
                        : String(option?.children || '');
                      return label.toLowerCase().includes(input.toLowerCase());
                    }}
                    optionFilterProp="children"
                  >
                    <Option value="">None (Root Artifact)</Option>
                    {parentArtifacts
                      .filter(artifact => artifact.id !== artifactId) // Don't allow self as parent
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((artifact) => (
                        <Option key={artifact.id} value={artifact.id}>
                          {artifact.name}
                        </Option>
                      ))}
                  </Select>
                </Form.Item>

                <Form.Item
                  label="State Template"
                  name="state_template_id"
                  tooltip="Select a state template to apply to this artifact. If not selected, the artifact type template will be used."
                >
                  <Select 
                    placeholder="Select state template (optional)"
                    allowClear
                    showSearch
                    loading={loadingStateTemplates}
                    filterOption={(input, option) => {
                      const label = typeof option?.children === 'string' 
                        ? option.children 
                        : String(option?.children || '');
                      return label.toLowerCase().includes(input.toLowerCase());
                    }}
                    optionFilterProp="children"
                  >
                    <Option value="">None (use artifact type template)</Option>
                    {stateTemplates
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((template) => (
                        <Option key={template.id} value={template.id}>
                          {template.name}{template.version ? ` (v${template.version})` : ''}
                        </Option>
                      ))}
                  </Select>
                </Form.Item>

                <Form.Item
                  label="Creation Mode"
                  name="creation_mode"
                  tooltip="Controls how artifact items can be created at runtime"
                >
                  <Select 
                    placeholder="Select creation mode"
                    allowClear
                  >
                    <Option value="any">Any - Can be created directly or by another artifact</Option>
                    <Option value="self_only">Self Only - Can only be created directly (POST to artifact-items)</Option>
                    <Option value="triggered_only">Triggered Only - Can only be created by a parent artifact's transition</Option>
                  </Select>
                </Form.Item>
                {form.getFieldValue('creation_mode') === 'triggered_only' && (
                  <div style={{ 
                    background: '#fff7e6', 
                    border: '1px solid #ffd591', 
                    borderRadius: '4px', 
                    padding: '8px 12px',
                    marginTop: '-8px',
                    marginBottom: '16px',
                    fontSize: '13px',
                    color: '#ad6800'
                  }}>
                    <strong>Warning:</strong> "Triggered Only" means artifact items can only be created when a parent artifact's transition triggers their creation. Direct API creation will be blocked.
                  </div>
                )}
              </div>

              {/* Description field at bottom with helper text */}
              <div>
                <div style={{ marginBottom: '8px' }}>
                  <label style={{ fontWeight: 500, color: '#374151', display: 'block', marginBottom: '4px' }}>
                    Description
                    <span style={{ fontSize: '12px', fontWeight: 'normal', color: '#666', marginLeft: '8px' }}>
                      (Markdown supported - used for comprehensive artifact context and Krybot agent instructions)
                    </span>
                  </label>
                </div>
                
                {/* Collapsible helper text */}
                <Collapse
                  ghost
                  style={{ marginBottom: '12px' }}
                  items={[
                    {
                      key: 'guidelines',
                      label: (
                        <span>
                          <InfoCircleOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                          What to include in Description
                        </span>
                      ),
                      children: (
                        <div style={{ padding: '8px 0' }}>
                          <div style={{ marginBottom: '12px' }}>
                            <strong>Artifact Description:</strong> How is it used with a few examples. Describe the primary function and typical use cases of this artifact.
                          </div>
                          <div style={{ marginBottom: '12px' }}>
                            <strong>Artifact Purpose:</strong> What is the reason this artifact exists and who or what benefits. Explain the business value and stakeholders.
                          </div>
                          <div style={{ marginBottom: '12px' }}>
                            <strong>Artifact Outputs:</strong> What if anything is generated by the artifact or how is the artifact measured. Describe deliverables, metrics, or outcomes.
                          </div>
                          <div style={{ marginBottom: '12px' }}>
                            <strong>Artifact Inputs:</strong> Who or what is needed for the artifact to operate. List dependencies, resources, or prerequisites.
                          </div>
                          <div>
                            <strong>Creation:</strong> How is the artifact created and with what resources. Explain the creation process, required resources, and any constraints.
                          </div>
                        </div>
                      ),
                    },
                  ]}
                />

                <Tabs
                  type="card"
                  items={[
                    {
                      key: 'edit',
                      label: (
                        <span>
                          <EditOutlined /> Edit
                        </span>
                      ),
                      children: (
                        <Form.Item name="description" noStyle>
                          <TextArea
                            rows={20}
                            placeholder="Enter artifact description using Markdown syntax. This description provides comprehensive context about the artifact and is used by Krybot agents for better understanding and definition of artifact elements."
                            style={{ fontFamily: 'monospace', resize: 'vertical', minHeight: '600px' }}
                            onChange={(e) => {
                              setDescriptionPreview(e.target.value);
                            }}
                          />
                        </Form.Item>
                      ),
                    },
                      {
                        key: 'preview',
                        label: (
                          <span>
                            <EyeOutlined /> Preview
                          </span>
                        ),
                        children: (
                          <div
                            className="markdown-preview"
                            style={{
                              minHeight: '600px',
                              padding: '12px',
                              border: '1px solid #d9d9d9',
                              borderRadius: '4px',
                              backgroundColor: '#fafafa',
                              overflow: 'auto',
                            }}
                          >
                            {descriptionPreview ? (
                              <ReactMarkdown>{descriptionPreview}</ReactMarkdown>
                            ) : (
                              <div style={{ color: '#999', fontStyle: 'italic' }}>
                                No description provided. Preview will appear here when you add content.
                              </div>
                            )}
                          </div>
                        ),
                      },
                    ]}
                  />
              </div>
            </div>

          </Form>
        </div>
      </Card>
    </div>
  );
};
